/**
 * Client-side enhancements for tort.
 *
 * Most of the UI is server-rendered HTMX. This file handles the one thing
 * HTMX doesn't do well on its own: streaming the agent's workup progress
 * via Server-Sent Events into a live-updating report panel.
 */

type WorkupEvent =
  | { type: "status"; message: string }
  | { type: "section"; key: string; title: string; markdown: string }
  | { type: "tool_call"; name: string; input: unknown }
  | { type: "tool_result"; name: string; ok: boolean; summary: string }
  | { type: "done"; case_id: string }
  | { type: "error"; message: string };

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Partial<Record<string, string>> = {},
  children: (Node | string)[] = []
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v !== undefined) node.setAttribute(k, v);
  }
  for (const c of children) {
    node.append(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return node;
}

function appendStatus(log: HTMLElement, msg: string): void {
  const row = el("div", { class: "text-xs text-zinc-500 font-mono" }, [
    new Date().toLocaleTimeString() + "  " + msg,
  ]);
  log.append(row);
  log.scrollTop = log.scrollHeight;
}

function upsertSection(
  panel: HTMLElement,
  key: string,
  title: string,
  markdown: string
): void {
  let section = panel.querySelector<HTMLElement>(`[data-section="${key}"]`);
  if (!section) {
    section = el("section", {
      "data-section": key,
      class: "mb-6 border-l-2 border-zinc-200 pl-4",
    });
    section.append(
      el("h3", { class: "text-base font-semibold mb-2" }, [title])
    );
    section.append(el("div", { class: "prose prose-sm max-w-none" }));
    panel.append(section);
  }
  const body = section.querySelector<HTMLElement>("div.prose")!;
  body.textContent = markdown;
}

function startWorkupStream(caseId: string): void {
  const log = document.getElementById("workup-log");
  const panel = document.getElementById("workup-report");
  if (!log || !panel) return;

  const src = new EventSource(`/cases/${caseId}/stream`);

  src.onmessage = (e) => {
    let evt: WorkupEvent;
    try {
      evt = JSON.parse(e.data) as WorkupEvent;
    } catch {
      return;
    }
    switch (evt.type) {
      case "status":
        appendStatus(log, evt.message);
        break;
      case "section":
        upsertSection(panel, evt.key, evt.title, evt.markdown);
        break;
      case "tool_call":
        appendStatus(log, `→ ${evt.name}`);
        break;
      case "tool_result":
        appendStatus(log, `← ${evt.name}: ${evt.summary}`);
        break;
      case "done":
        appendStatus(log, "workup complete");
        src.close();
        break;
      case "error":
        appendStatus(log, `ERROR: ${evt.message}`);
        src.close();
        break;
    }
  };

  src.onerror = () => {
    appendStatus(log, "stream interrupted");
    src.close();
  };
}

declare global {
  interface Window {
    tort: { startWorkupStream: (caseId: string) => void };
  }
}

window.tort = { startWorkupStream };
